import { GoogleCloudRestService, GoogleCloudRestConfig } from './GoogleCloudRestService';
import { AudioProcessor, AudioProcessorConfig } from './AudioProcessor';

export interface GoogleCloudTranscriptionConfig {
  apiKey: string;
  projectId: string;
  languageCode?: string;
  enableInterimResults?: boolean;
  enableSpeakerDiarization?: boolean;
  maxSpeakers?: number;
  model?: string;
  sampleRate?: number;
  audioChannels?: number;
}

export interface TranscriptionResult {
  text: string;
  isFinal: boolean;
  confidence: number;
  speakerTag?: number;
  startTime?: string;
  endTime?: string;
  audioSource: 'local' | 'remote' | 'mixed';
}

export interface TranscriptionCallback {
  (result: TranscriptionResult): void;
}

export class GoogleCloudTranscriptionService {
  private config: GoogleCloudTranscriptionConfig;
  private restService: GoogleCloudRestService;
  private audioProcessor: AudioProcessor;
  private isActive: boolean = false;
  private transcriptionCallback: TranscriptionCallback | null = null;
  private localStream: MediaStream | null = null;
  private remoteStream: MediaStream | null = null;

  constructor(config: GoogleCloudTranscriptionConfig) {
    this.config = {
      languageCode: 'en-US',
      enableInterimResults: true,
      enableSpeakerDiarization: true,
      maxSpeakers: 2,
      model: 'phone_call',
      sampleRate: 16000,
      audioChannels: 1,
      ...config
    };

    // Initialize REST service
    const restConfig: GoogleCloudRestConfig = {
      apiKey: this.config.apiKey,
      projectId: this.config.projectId,
      languageCode: this.config.languageCode,
      enableInterimResults: this.config.enableInterimResults,
      enableSpeakerDiarization: this.config.enableSpeakerDiarization,
      maxSpeakers: this.config.maxSpeakers,
      model: this.config.model,
      sampleRate: this.config.sampleRate,
      audioChannels: this.config.audioChannels
    };

    this.restService = new GoogleCloudRestService(restConfig);

    // Initialize audio processor
    const audioConfig: AudioProcessorConfig = {
      sampleRate: this.config.sampleRate!,
      channels: this.config.audioChannels!,
      bitDepth: 16,
      bufferSize: 4096
    };

    this.audioProcessor = new AudioProcessor(audioConfig);

    // Set up REST service callbacks
    this.setupRestServiceCallbacks();
  }

  /**
   * Check if Google Cloud transcription is supported
   */
  public static isSupported(): boolean {
    return GoogleCloudRestService.isSupported() && AudioProcessor.isSupported();
  }

  /**
   * Start dual-side transcription
   */
  public async startTranscription(
    callback: TranscriptionCallback,
    localStream?: MediaStream,
    remoteStream?: MediaStream
  ): Promise<boolean> {
    try {
      if (this.isActive) {
        this.stopTranscription();
      }

      this.transcriptionCallback = callback;
      this.localStream = localStream || null;
      this.remoteStream = remoteStream || null;

      // Start the REST service
      const restStarted = this.restService.startTranscription((result) => {
  
        if (this.transcriptionCallback) {
          this.transcriptionCallback(result);
        }
      });

      if (!restStarted) {
        console.error("❌ Failed to start REST service");
        return false;
      }

      // Initialize audio processor first
      const audioInitialized = await this.audioProcessor.initialize();
      if (!audioInitialized) {
        console.error("❌ Failed to initialize audio processor");
        return false;
      }

      // Start audio processing
      if (localStream && remoteStream) {
        const dualStarted = this.audioProcessor.processDualStreams(
          localStream,
          remoteStream,
          (audioData) => {
            this.restService.addAudioData(audioData);
          }
        );
        
        if (!dualStarted) {
          console.error("❌ Failed to start dual-stream processing");
          return false;
        }
      } else if (localStream) {
        const localStarted = this.audioProcessor.processAudioStream(
          localStream,
          (audioData) => {
            this.restService.addAudioData(audioData);
          }
        );
        
        if (!localStarted) {
          console.error("❌ Failed to start local stream processing");
          return false;
        }
      } else if (remoteStream) {
        const remoteStarted = this.audioProcessor.processAudioStream(
          remoteStream,
          (audioData) => {
            this.restService.addAudioData(audioData);
          }
        );
        
        if (!remoteStarted) {
          console.error("❌ Failed to start remote stream processing");
          return false;
        }
      } else {
        console.error("❌ No audio streams provided");
        return false;
      }

      this.isActive = true;

      return true;

    } catch (error) {
      console.error("❌ Transcription service failed:", error);
      return false;
    }
  }

  /**
   * Stop transcription
   */
  public stopTranscription(): void {
    this.isActive = false;
    
    if (this.restService) {
      this.restService.stopTranscription();
    }
    
    if (this.audioProcessor) {
      this.audioProcessor.stopProcessing();
    }
    
    this.transcriptionCallback = null;
    this.localStream = null;
    this.remoteStream = null;
  }

  /**
   * Check if transcription is active
   */
  public isTranscriptionActive(): boolean {
    return this.isActive && this.restService.isTranscriptionActive();
  }

  /**
   * Dispose of resources
   */
  public dispose(): void {
    this.stopTranscription();
    
    if (this.restService) {
      this.restService.dispose();
    }
    
    if (this.audioProcessor) {
      this.audioProcessor.dispose();
    }
  }

  /**
   * Set up REST service callbacks
   */
  private setupRestServiceCallbacks(): void {
    // The REST service handles its own callbacks
    // We just need to make sure it's properly initialized
  }
}
